import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from './useSocket';

/**
 * Custom hook for managing bot registry data and selections
 */
export const useBotRegistry = (gameType = null) => {
  const { i18n } = useTranslation();
  const { socket } = useSocket();

  const [bots, setBots] = useState([]);
  const [selectedBots, setSelectedBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState({ gameType: null, language: null });

  // Set up socket listeners once
  useEffect(() => {
    if (!socket) return;

    const handleBotsResponse = (data) => {
      setBots(data.bots || []);
      setLoading(false);
    };

    const handleBotsError = (errorData) => {
      setError(errorData.message || 'Failed to load bots');
      setLoading(false);
    };

    // Add listeners
    socket.on('availableBots', handleBotsResponse);
    socket.on('botsError', handleBotsError);

    // Cleanup on unmount or socket change
    return () => {
      socket.off('availableBots', handleBotsResponse);
      socket.off('botsError', handleBotsError);
    };
  }, [socket]);

  // Fetch bots when game type or language changes
  useEffect(() => {
    if (!gameType || !socket) {
      setBots([]);
      setSelectedBots([]);
      return;
    }

    const currentLanguage = i18n.language || 'en';

    // Prevent re-fetching if we already have this data
    if (lastFetch.gameType === gameType && lastFetch.language === currentLanguage) {
      return;
    }

    setLoading(true);
    setError(null);
    setLastFetch({ gameType, language: currentLanguage });

    // Request bots from server
    socket.emit('getBots', {
      gameType,
      language: currentLanguage
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, i18n.language]);

  // Bot selection management
  const addBot = useCallback((botId) => {
    const bot = bots.find(b => b.id === botId);
    if (bot && !selectedBots.find(b => b.id === botId)) {
      setSelectedBots(prev => [...prev, bot]);
    }
  }, [bots, selectedBots]);

  const removeBot = useCallback((botId) => {
    setSelectedBots(prev => prev.filter(bot => bot.id !== botId));
  }, []);

  const toggleBot = useCallback((botId) => {
    const isSelected = selectedBots.find(bot => bot.id === botId);
    if (isSelected) {
      removeBot(botId);
    } else {
      addBot(botId);
    }
  }, [selectedBots, addBot, removeBot]);

  const clearSelectedBots = useCallback(() => {
    setSelectedBots([]);
  }, []);

  const isBotSelected = useCallback((botId) => {
    return selectedBots.some(bot => bot.id === botId);
  }, [selectedBots]);

  // Get bots by difficulty level
  const getBotsByDifficulty = useCallback((difficulty) => {
    return bots.filter(bot => bot.difficulty === difficulty);
  }, [bots]);

  // Get bots by personality traits
  const getBotsByTrait = useCallback((trait) => {
    return bots.filter(bot => bot.personality.includes(trait));
  }, [bots]);

  // Get available difficulties
  const getAvailableDifficulties = useCallback(() => {
    return [...new Set(bots.map(bot => bot.difficulty))].sort();
  }, [bots]);

  // Get available personality traits
  const getAvailableTraits = useCallback(() => {
    return [...new Set(bots.flatMap(bot => bot.personality))].sort();
  }, [bots]);

  // Validate bot selection against game constraints
  const validateSelection = useCallback((maxBots = 6) => {
    return {
      isValid: selectedBots.length <= maxBots,
      count: selectedBots.length,
      maxBots,
      remaining: Math.max(0, maxBots - selectedBots.length)
    };
  }, [selectedBots]);

  return {
    // Data
    bots,
    selectedBots,
    loading,
    error,

    // Actions
    addBot,
    removeBot,
    toggleBot,
    clearSelectedBots,

    // Utilities
    isBotSelected,
    getBotsByDifficulty,
    getBotsByTrait,
    getAvailableDifficulties,
    getAvailableTraits,
    validateSelection
  };
};

export default useBotRegistry;